// File: src/components/landing/TestimonialsSection.tsx
import React from 'react';
import { Box, Container, Typography, Card, CardContent, Avatar, Grid } from '@mui/material';
import { FormatQuote } from '@mui/icons-material';

// Import images
import aliciaReidImage from '@/assets/testimonials/alicia-reid.jpg';
import iraCartyImage from '@/assets/testimonials/ira-carty-min.jpg';
import taraNataleImage from '@/assets/testimonials/tara-natale-min.jpg';

interface Testimonial {
  name: string;
  role: string;
  image: string;
  quote: string;
}

const testimonials: Testimonial[] = [
  {
    name: "Alicia Reid",
    role: "Former Student",
    image: aliciaReidImage,
    quote: "A platform integrated with schools to provide tailored volunteer opportunities would have been beneficial, reducing the need to independently seek opportunities and enhancing alignment with career goals."
  },
  {
    name: "Ira Carty",
    role: "Guidance Counselor",
    image: iraCartyImage,
    quote: "A platform that connects students with relevant volunteer opportunities based on their career interests would be highly beneficial. It would also reduce the workload on guidance counselors and streamline the process for tracking volunteer hours."
  },
  {
    name: "Tara Natale",
    role: "Non-Profit Director & Business Consultant",
    image: taraNataleImage,
    quote: "There's a real need for a platform that connects students with volunteer opportunities aligned with their interests. It would make finding meaningful volunteer work easier and help students gain relevant experience."
  }
];

const TestimonialsSection: React.FC = () => {
  return (
    <Box sx={{ width: '100%' }}>
      <Container maxWidth={false} sx={{ width: '100%', px: { xs: 2, sm: 4, md: 6 } }}>
        <Typography
          variant="h2"
          align="center"
          gutterBottom
          sx={{ 
            mb: 6,
            fontSize: { xs: '2rem', md: '2.75rem' },
            fontWeight: 700,
            background: 'linear-gradient(45deg, #6B46C1 30%, #4C51BF 90%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          What Our Supporters Say
        </Typography>
        <Grid container spacing={4}>
          {testimonials.map((testimonial) => (
            <Grid item xs={12} md={4} key={testimonial.name}>
              <Card 
                sx={{ 
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 12px 32px rgba(107, 70, 193, 0.15)'
                  },
                  border: '1px solid rgba(107, 70, 193, 0.1)',
                  borderRadius: 2
                }}
              >
                <CardContent>
                  <Box sx={{ mb: 2, color: 'primary.main' }}>
                    <FormatQuote sx={{ fontSize: 40 }} />
                  </Box>
                  <Typography 
                    variant="body1" 
                    paragraph
                    sx={{ mb: 3, fontStyle: 'italic' }}
                  >
                    "{testimonial.quote}"
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 'auto' }}>
                    <Avatar
                      src={testimonial.image}
                      alt={testimonial.name}
                      sx={{ width: 48, height: 48, mr: 2 }}
                    />
                    <Box>
                      <Typography variant="subtitle1">
                        {testimonial.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {testimonial.role}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default TestimonialsSection;